import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";

// Core components
import UserHeader from "components/Headers/UserHeader.js";
import { getadmindetaildbyid, updateAdminProfile } from "../../api/ApiServices";

import Swal from "sweetalert2";
import avatar from "assets/img/brand/avatar-2.jpg";

// Get admin ID from localStorage
const getAdminId = () => {
  const userData = localStorage.getItem("userData");
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.uuid || parsed.id || parsed._id || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Get image base URL from environment variable
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";


const Profile = () => {
  // =========================================================
  // State Variables
  // =========================================================
  const [adminData, setAdminData] = useState({
    uuid: "",
    fullName: "",
    email: "",
    mobileNumber: "",
    country: "",
    address: "",
    image: "",
    imageFile: null,
    password: "",
    confirmPassword: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [errors, setErrors] = useState({
    fullName: false,
    email: false,
    mobileNumber: false,
    country: false,
    address: false,
    password: false,
    confirmPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const fileInputRef = useRef(null);

  // =========================================================
  // Helper: Get Full Image URL
  // =========================================================
  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return avatar;
    }

    let clean = String(imagePath).trim().replace(/^["']|["']$/g, "").replace(/%22$/gi, "");
    if (!clean) {
      return avatar;
    }

    // Newly selected local image or complete URL
    if (
      clean.startsWith("blob:") ||
      clean.startsWith("http://") ||
      clean.startsWith("https://")
    ) {
      return clean;
    }

    const cleanPath = clean.replace(/^\/+/, "");
    if (cleanPath.startsWith("admin-profile-images/") || cleanPath.startsWith("users-profile-images/")) {
      return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/${cleanPath}`;
    }
    return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/admin-profile-images/${cleanPath}`;
  };

  // =========================================================
  // Fetch Admin Details
  // =========================================================
  const fetchAdminDetails = async () => {
    setLoading(true);

    try {
      const adminId = localStorage.getItem("adminId") || getAdminId();

      if (!adminId) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Admin ID not found. Please login again.",
        });
        setLoading(false);
        return;
      }

      const response = await getadmindetaildbyid(adminId);

      console.log("Fetch Response:", response);

      if (!response) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No response received from server.",
        });
        setLoading(false);
        return;
      }

      if (!response.success) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to fetch admin details",
        });
        setLoading(false);
        return;
      }

      let admin = response?.data || response?.payload || response;

      if (Array.isArray(admin)) {
        admin = admin[0] || {};
      }

      if (!admin || typeof admin !== "object" || Object.keys(admin).length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Profile Not Found",
          text: "No admin profile found.",
        });
        setLoading(false);
        return;
      }

      // ==========================================
      // GET IMAGE URL - PRIORITIZE imageUrl
      // ==========================================
      const imageUrl = admin.imageUrl || admin.image || "";

      console.log("Admin Image Key:", admin.image);
      console.log("Admin Image URL:", imageUrl);

      const formData = {
        uuid: admin.uuid || admin._id || adminId,
        fullName: admin.fullName || admin.name || "",
        email: admin.email || "",
        mobileNumber: admin.mobileNumber || admin.phone || "",
        country: admin.country || "",
        address: admin.address || "",
        image: imageUrl, // Use the signed URL directly
        imageFile: null,
        password: "",
        confirmPassword: "",
        isActive: admin.isActive !== undefined ? admin.isActive : true,
      };

      if (!formData.uuid) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "Admin profile UUID not found.",
        });
        setLoading(false);
        return;
      }

      setAdminData(formData);
    } catch (error) {
      console.error("Fetch Admin Details Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Something went wrong while fetching admin details.",
      });
    } finally {
      setLoading(false);
    }
  };
  // =========================================================
  // Load Data on Mount
  // =========================================================
  useEffect(() => {
    fetchAdminDetails();
  }, []);

  // =========================================================
  // Image Select
  // =========================================================
  const handleImageClick = (e) => {
    e.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;


    if (!file.type.startsWith("image/")) {
      Swal.fire({
        title: "Invalid File!",
        text: "Please select a valid image.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);

    setAdminData((prev) => ({
      ...prev,
      image: previewUrl,
      imageFile: file,
    }));

    window.dispatchEvent(
      new CustomEvent("adminProfileImageUpdated", {
        detail: previewUrl,
      })
    );
  };


  const handleUpdate = async (e) => {
    e.preventDefault();

    const newErrors = {
      fullName: !adminData.fullName?.trim(),
      email: !adminData.email?.trim(),
      mobileNumber: !adminData.mobileNumber?.toString().trim(),
      country: false,
      address: false,
      password: adminData.password && adminData.password.length < 6 ? true : false,
      confirmPassword: adminData.password && adminData.password !== adminData.confirmPassword ? true : false,
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error);

    if (hasErrors) {
      Swal.fire({
        title: "Validation Error!",
        text: "Please fill all required fields correctly.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!adminData.uuid) {
      Swal.fire({
        title: "Error!",
        text: "Admin UUID is missing. Please refresh and try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    setUpdating(true);

    try {
      const formData = new FormData();

      formData.append("fullName", adminData.fullName || "");
      formData.append("email", adminData.email || "");
      formData.append("mobileNumber", adminData.mobileNumber || "");
      formData.append("country", adminData.country || "");
      formData.append("address", adminData.address || "");

      if (adminData.password) {
        formData.append("password", adminData.password);
      }

      if (adminData.imageFile) {
        console.log("Uploading new image:", adminData.imageFile);
        formData.append("image", adminData.imageFile);
        formData.append("profileImage", adminData.imageFile);
      } else {
        console.log("No new image selected");
      }

      const response = await updateAdminProfile(adminData.uuid, formData);

      console.log("========== UPDATE RESPONSE ==========");
      console.log("Full response:", response);

      if (response?.success) {
        const responseData = response.data || {};

        console.log("Response Data:", responseData);
        console.log("Image from response:", responseData.image);
        console.log("ImageUrl from response:", responseData.imageUrl);

        // Update localStorage
        try {
          const existingUserData = JSON.parse(localStorage.getItem("userData") || "{}");
          const updatedUserData = { ...existingUserData, ...responseData };
          localStorage.setItem("userData", JSON.stringify(updatedUserData));
          if (responseData.fullName) {
            localStorage.setItem("userName", responseData.fullName);
          }
        } catch (e) {
          console.error("Error updating localStorage userData:", e);
        }

        // UPDATE STATE
        setAdminData((prev) => ({
          ...prev,
          fullName: responseData.fullName || prev.fullName,
          email: responseData.email || prev.email,
          mobileNumber: responseData.mobileNumber || prev.mobileNumber,
          country: responseData.country || prev.country,
          address: responseData.address || prev.address,
          image: responseData.imageUrl || responseData.image || prev.image,
          imageFile: null,
          password: "",
          confirmPassword: "",
        }));

        // DISPATCH EVENT FOR HEADER UPDATE
        const updatedImg = responseData.imageUrl || responseData.image;
        if (updatedImg) {
          window.dispatchEvent(
            new CustomEvent("adminProfileImageUpdated", {
              detail: updatedImg,
            })
          );
        }

        Swal.fire({
          title: "Profile Updated!",
          text: response.message || "Admin profile has been updated successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          title: "Update Failed!",
          text: response?.message || "Unable to update admin profile.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error updating admin profile:", error);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || error?.message || "Something went wrong",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAdminData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <UserHeader adminData={adminData} />

      <Container className="mt--7" fluid>
        <Row>
          <Col xl="4" className="order-xl-2 mb-5 mb-xl-0">

            <Card className="shadow border-0 h-100">
              {loading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                  <p className="mt-2">Loading Profile Details ...</p>
                </div>
              ) : (
                <>
                  <CardHeader className="bg-transparent border-0 pb-0">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h3 className="mb-1 text-dark font-weight-700">
                          <i
                            className="ni ni-single-02 mr-2"
                            style={{
                              color: "var(--button-primary)",
                              verticalAlign: "middle",
                            }}
                          />
                          My Profile
                        </h3>

                        <p className="text-muted mb-0 small">
                          Manage your personal profile information
                        </p>
                      </div>

                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "42px",
                          height: "42px",
                          background: "var(--button-primary)",
                        }}
                      >
                        <i className="ni ni-single-02 text-white" />
                      </div>
                    </div>
                  </CardHeader>

                  <CardBody>
                    <div className="text-center mb-4">
                      <div
                        style={{
                          position: "relative",
                          width: "100px",
                          height: "100px",
                        }}
                      >
                        <img
                          alt="Admin Profile"
                          className="rounded-circle"
                          src={getImageUrl(adminData.image)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(e) => {
                            const imagePath = adminData.image;

                            if (
                              imagePath &&
                              !imagePath.startsWith("blob:") &&
                              !imagePath.startsWith("http")
                            ) {
                              const baseUrl = IMAGE_BASE_URL.replace(/\/+$/, "");
                              const path = imagePath.replace(/^\/+/, "");
                              const fullUrl = `${baseUrl}/${path}`;
                            }

                            e.currentTarget.onerror = null;
                            e.currentTarget.src = avatar;
                          }}
                        />

                        {/* Edit Icon */}
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            position: "absolute",
                            bottom: "0",
                            right: "0",
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            border: "2px solid #fff",
                            background: "#fff",
                            color: "#007bff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0",
                            cursor: "pointer",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          }}
                          onClick={handleImageClick}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      </div>

                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </div>

                    <div className="text-center mb-4">
                      <h2 className="mb-1 text-dark font-weight-700">
                        {adminData.fullName || "Admin User"}
                      </h2>

                      <p className="text-muted mb-0">
                        Area Track Administrator
                      </p>
                    </div>

                    <hr className="my-4" />

                    <div className="d-flex align-items-start mb-4">
                      <div
                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                        style={{
                          minWidth: "40px",
                          height: "40px",
                          background: "rgba(0, 123, 255, 0.1)",
                        }}
                      >
                        <i
                          className="ni ni-email-83"
                          style={{
                            color: "var(--button-primary)",
                            fontSize: "18px",
                          }}
                        />
                      </div>

                      <div>
                        <small className="text-muted d-block mb-1">
                          Email Address
                        </small>

                        <span className="font-weight-600 text-dark">
                          {adminData.email || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-start mb-4">
                      <div
                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                        style={{
                          minWidth: "40px",
                          height: "40px",
                          background: "rgba(255, 193, 7, 0.12)",
                        }}
                      >
                        <i
                          className="ni ni-pin-3"
                          style={{
                            color: "#ffc107",
                            fontSize: "18px",
                          }}
                        />
                      </div>

                      <div>
                        <small className="text-muted d-block mb-1">
                          Location
                        </small>

                        <span className="font-weight-600 text-dark">
                          {adminData.address || adminData.country || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-start">
                      <div
                        className="mr-3 d-flex align-items-center justify-content-center rounded"
                        style={{
                          minWidth: "40px",
                          height: "40px",
                          background: "rgba(40, 167, 69, 0.1)",
                        }}
                      >
                        <i
                          className="ni ni-favourite-28"
                          style={{
                            color: "#28a745",
                            fontSize: "18px",
                          }}
                        />
                      </div>

                      <div>
                        <small className="text-muted d-block mb-1">
                          Application
                        </small>

                        <span className="font-weight-600 text-dark">
                          Manage Area Track
                        </span>
                      </div>
                    </div>

                    <div
                      className="mt-5 p-3 rounded"
                      style={{
                        background: "rgba(0, 0, 0, 0.03)",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <i
                          className="ni ni-check-bold mr-2"
                          style={{
                            color: "var(--button-primary)",
                          }}
                        />

                        <small className="text-muted">
                          Your profile information is securely managed
                          from this account.
                        </small>
                      </div>
                    </div>
                  </CardBody></>
              )}
            </Card>
          </Col>

          <Col xl="8" className="order-xl-1">
            <Card className="shadow border-0">
              {loading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                  <p className="mt-2">Loading Details ...</p>
                </div>
              ) : (
                <>
                  <CardHeader className="bg-transparent border-0 py-4">
                    <Row className="align-items-center">
                      <Col>
                        <h3 className="mb-1 text-dark font-weight-700">
                          <i
                            className="ni ni-settings-gear-65 mr-2"
                            style={{
                              color: "var(--button-primary)",
                              verticalAlign: "middle",
                            }}
                          />
                          My Account
                        </h3>

                        <p className="text-muted mb-0 small">
                          Manage your personal information and account
                          settings
                        </p>
                      </Col>

                      <Col xs="auto">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: "42px",
                            height: "42px",
                            background: "rgba(0, 123, 255, 0.1)",
                          }}
                        >
                          <i
                            className="ni ni-single-02"
                            style={{
                              color: "var(--button-primary)",
                              fontSize: "20px",
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </CardHeader>

                  <CardBody className="pt-2">
                    <Form>
                      <div className="mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className="mr-2 d-flex align-items-center justify-content-center rounded"
                            style={{
                              width: "34px",
                              height: "34px",
                              background: "rgba(0, 123, 255, 0.1)",
                            }}
                          >
                            <i
                              className="ni ni-single-02"
                              style={{
                                color: "var(--button-primary)",
                                fontSize: "17px",
                              }}
                            />
                          </div>

                          <div>
                            <h6 className="heading-small text-muted mb-0">
                              User Information
                            </h6>

                            <small className="text-muted">
                              Update your personal account information
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="pl-lg-4">
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-fullname"
                              >
                                Full Name{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <Input
                                className={`form-control-alternative ${errors.fullName ? "border-danger" : ""
                                  }`}
                                value={adminData.fullName}
                                id="input-fullname"
                                placeholder="Full Name"
                                type="text"
                                onChange={(e) =>
                                  handleInputChange(
                                    "fullName",
                                    e.target.value
                                  )
                                }
                                disabled={updating}
                              />

                              {errors.fullName && (
                                <small className="text-danger mt-1 d-block">
                                  Full Name is required.
                                </small>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-email"
                              >
                                Email <span className="text-danger">*</span>
                              </label>

                              <Input
                                className={`form-control-alternative ${errors.email ? "border-danger" : ""
                                  }`}
                                id="input-email"
                                placeholder="Email"
                                type="email"
                                value={adminData.email || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    "email",
                                    e.target.value
                                  )
                                }
                                disabled={updating}
                              />

                              {errors.email && (
                                <small className="text-danger mt-1 d-block">
                                  Email is required.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-mobile"
                              >
                                Mobile Number{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <Input
                                className={`form-control-alternative ${errors.mobileNumber
                                  ? "border-danger"
                                  : ""
                                  }`}
                                value={adminData.mobileNumber}
                                id="input-mobile"
                                placeholder="Mobile Number"
                                maxLength={10}
                                type="text"
                                onChange={(e) => {
                                  const onlyNums =
                                    e.target.value.replace(
                                      /[^0-9]/g,
                                      ""
                                    );

                                  handleInputChange(
                                    "mobileNumber",
                                    onlyNums
                                  );
                                }}
                                disabled={updating}
                              />

                              {errors.mobileNumber && (
                                <small className="text-danger mt-1 d-block">
                                  Mobile Number is required.
                                </small>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-country"
                              >
                                Country{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <Input
                                className={`form-control-alternative ${errors.country ? "border-danger" : ""
                                  }`}
                                id="input-country"
                                placeholder="Country"
                                type="text"
                                value={adminData.country || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    "country",
                                    e.target.value
                                  )
                                }
                                disabled={updating}
                              />

                              {errors.country && (
                                <small className="text-danger mt-1 d-block">
                                  Country is required.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md="12">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-address"
                              >
                                Address{" "}
                                <span className="text-danger">*</span>
                              </label>

                              <Input
                                className={`form-control-alternative ${errors.address ? "border-danger" : ""
                                  }`}
                                id="input-address"
                                placeholder="Address"
                                type="text"
                                value={adminData.address || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    "address",
                                    e.target.value
                                  )
                                }
                                disabled={updating}
                              />

                              {errors.address && (
                                <small className="text-danger mt-1 d-block">
                                  Address is required.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>

                      <hr className="my-4" />

                      <div className="mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className="mr-2 d-flex align-items-center justify-content-center rounded"
                            style={{
                              width: "34px",
                              height: "34px",
                              background: "rgba(255, 193, 7, 0.12)",
                            }}
                          >
                            <i
                              className="ni ni-lock-circle-open"
                              style={{
                                color: "#ffc107",
                                fontSize: "17px",
                              }}
                            />
                          </div>

                          <div>
                            <h6 className="heading-small text-muted mb-0">
                              Change Password
                            </h6>

                            <small className="text-muted">
                              Keep your account secure with a strong
                              password
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="pl-lg-4">
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label">
                                New Password
                              </label>

                              <InputGroup className="input-group-alternative">
                                <Input
                                  type={
                                    showPassword
                                      ? "text"
                                      : "password"
                                  }
                                  placeholder="Enter new password"
                                  value={adminData.password}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "password",
                                      e.target.value
                                    )
                                  }
                                  className={`form-control-alternative ${errors.password
                                    ? "border-danger"
                                    : ""
                                    }`}
                                  disabled={updating}
                                />

                                <InputGroupAddon addonType="append">
                                  <InputGroupText
                                    onClick={() =>
                                      setShowPassword(
                                        !showPassword
                                      )
                                    }
                                    style={{
                                      cursor: "pointer",
                                    }}
                                  >
                                    <i
                                      className={
                                        showPassword
                                          ? "fas fa-eye-slash"
                                          : "fas fa-eye"
                                      }
                                    />
                                  </InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>

                              {errors.password && (
                                <small className="text-danger mt-1 d-block">
                                  Password must be at least 6
                                  characters.
                                </small>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label">
                                Confirm Password
                              </label>

                              <InputGroup className="input-group-alternative">
                                <Input
                                  type={
                                    showConfirmPassword
                                      ? "text"
                                      : "password"
                                  }
                                  placeholder="Confirm new password"
                                  value={adminData.confirmPassword}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "confirmPassword",
                                      e.target.value
                                    )
                                  }
                                  className={`form-control-alternative ${errors.confirmPassword
                                    ? "border-danger"
                                    : ""
                                    }`}
                                  disabled={updating}
                                />

                                <InputGroupAddon addonType="append">
                                  <InputGroupText
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword
                                      )
                                    }
                                    style={{
                                      cursor: "pointer",
                                    }}
                                  >
                                    <i
                                      className={
                                        showConfirmPassword
                                          ? "fas fa-eye-slash"
                                          : "fas fa-eye"
                                      }
                                    />
                                  </InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>

                              {errors.confirmPassword && (
                                <small className="text-danger mt-1 d-block">
                                  Passwords do not match.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>

                      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                        <Button
                          color="primary"
                          onClick={handleUpdate}
                          size="sm"
                          disabled={
                            updating || !adminData.uuid
                          }
                          className="px-4 button-secondary bg-button-gradient-primary text-nowrap"
                          style={{
                            borderRadius: "8px",
                            border: "none",
                          }}
                        >
                          {updating ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader" />
                              Updating...
                            </>
                          ) : (
                            "Update"
                          )}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </>)}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Profile;